import { QuestionCircleOutlined } from '@ant-design/icons'
import { InputNumber, Tooltip } from 'antd'
import classNames from 'classnames'
import { useAtom } from 'jotai'
import { MySlider } from '~/components/ui/MySlider'
import { MJQuality } from '~/graphqls/customType'
import styles from '../AiForm.module.css'
import { SeedComp } from './SeedComp'
import {
  mjQualityAtom,
  mjTileAtom,
  mjUpbetaAtom,
  styleArtisticAtom,
  styleDiffAtom,
} from './atom'
import { useEffect, useMemo } from 'react'
import { useType } from '../useType'

export const MjParamsComp = () => {
  const [styleDiff, setStyleDiff] = useAtom(styleDiffAtom)
  const [styleArtistic, setStyleArtistic] = useAtom(styleArtisticAtom)
  const [mjQuality, setMjQuality] = useAtom(mjQualityAtom)
  const [mjUpbeta, setMjUpbeta] = useAtom(mjUpbetaAtom)
  const [mjTile, setMjTile] = useAtom(mjTileAtom)
  const { isMJ61, isMJ7 } = useType()

  const qualityOptions = useMemo(() => {
    if (isMJ61) {
      return [
        {
          value: MJQuality.QUALITY_050,
          label: '0.5',
        },
        {
          value: MJQuality.QUALITY_100,
          label: '1',
        },
        {
          value: MJQuality.QUALITY_200,
          label: '2',
        },
      ]
    } else {
      return [
        {
          value: MJQuality.QUALITY_025,
          label: '0.25',
        },
        {
          value: MJQuality.QUALITY_050,
          label: '0.5',
        },
        {
          value: MJQuality.QUALITY_100,
          label: '1',
        },
      ]
    }
  }, [isMJ61])

  useEffect(() => {
    if (qualityOptions.some(it => it.value === mjQuality)) {
      return
    }
    setMjQuality(MJQuality.QUALITY_100)
  }, [qualityOptions])

  return (
    <div className="mb-8">
      <SeedComp disabled={false} />
      <div className="flex items-center mb-4 pt-1">
        <span className="text-neutral-500 w-[120px]">
          风格差异化{' '}
          <Tooltip
            placement="topLeft"
            arrowPointAtCenter
            title="Chaos（混乱），值越高，生成的（4张）图片风格差异化越大。"
          >
            <QuestionCircleOutlined />
          </Tooltip>
        </span>
        <MySlider
          className="flex-1"
          step={1}
          value={styleDiff}
          onChange={val => {
            setStyleDiff(val)
          }}
        />
        <InputNumber
          min={0}
          max={100}
          className={styles.inputNumberGroup}
          value={styleDiff}
          onChange={val => {
            setStyleDiff(Number(val?.toFixed(0)))
          }}
        />
      </div>
      <div className="flex items-center mb-4">
        <span className="text-neutral-500 w-[120px]">
          风格艺术化{' '}
          <Tooltip
            placement="topLeft"
            arrowPointAtCenter
            title="Stylize（风格化），值越高，越偏离你的描述，艺术性强。"
          >
            <QuestionCircleOutlined />
          </Tooltip>
        </span>
        <MySlider
          className="flex-1"
          step={1}
          max={1000}
          value={styleArtistic}
          onChange={val => {
            setStyleArtistic(val)
          }}
        />
        <InputNumber
          min={0}
          max={1000}
          className={styles.inputNumberGroup}
          value={styleArtistic}
          onChange={val => {
            setStyleArtistic(Number(val?.toFixed(0)))
          }}
        />
      </div>
      {!isMJ7 && (
        <div className="flex items-center mb-4">
          <span className="text-neutral-500 w-[120px]">
            QUALITY{' '}
            <Tooltip
              placement="topLeft"
              arrowPointAtCenter
              title="质量，值越高，画面细节质量越高，耗时长。低值适合手势抽象外观，高值可以改善受有许多细节的图像。"
            >
              <QuestionCircleOutlined />
            </Tooltip>
          </span>
          <div className="p-[2px] rounded bg-neutral-200 flex items-center text-xs leading-4 text-center cursor-pointer m-[10px]">
            {qualityOptions.map(option => (
              <div
                key={option.value}
                className={classNames(
                  'w-8 h-4 rounded-sm mr-1 text-xs leading-4 font-medium',
                  {
                    'bg-primary text-neutral-800': mjQuality === option.value,
                  }
                )}
                onClick={() => setMjQuality(option.value)}
              >
                {option.label}
              </div>
            ))}
          </div>
        </div>
      )}
      {!isMJ7 && (
        <>
          <div className="flex items-center mb-4">
            <span className="text-neutral-500 w-[120px]">
              UPBETA{' '}
              <Tooltip
                placement="topLeft"
                arrowPointAtCenter
                title="增加画面细节，提升画面质感，如让人的皱纹更加明显。"
              >
                <QuestionCircleOutlined />
              </Tooltip>
            </span>
            <div className="p-[2px] rounded bg-neutral-200 flex items-center text-xs leading-4 text-center cursor-pointer m-[10px]">
              <div
                className={classNames(
                  'w-4 h-4 rounded-sm mr-1 text-xs leading-4 font-medium',
                  {
                    'bg-primary text-neutral-800': mjUpbeta,
                  }
                )}
                onClick={() => setMjUpbeta(true)}
              >
                是
              </div>
              <div
                className={classNames(
                  'w-4 h-4 rounded-sm text-xs leading-4 font-medium',
                  {
                    'bg-primary text-neutral-800': !mjUpbeta,
                  }
                )}
                onClick={() => setMjUpbeta(false)}
              >
                否
              </div>
            </div>
          </div>
          <div className="flex items-center mb-4">
            <span className="text-neutral-500 w-[120px]">
              TILE{' '}
              <Tooltip
                placement="topLeft"
                arrowPointAtCenter
                title="无缝贴图，此功能生成的图像可用作重复图块，适合织物、纹理、壁纸等无缝图案。"
              >
                <QuestionCircleOutlined />
              </Tooltip>
            </span>
            <div className="p-[2px] rounded bg-neutral-200 flex items-center text-xs leading-4 text-center cursor-pointer m-[10px]">
              <div
                className={classNames(
                  'w-4 h-4 rounded-sm mr-1 text-xs leading-4 font-medium',
                  {
                    'bg-primary text-neutral-800': mjTile,
                  }
                )}
                onClick={() => setMjTile(true)}
              >
                是
              </div>
              <div
                className={classNames(
                  'w-4 h-4 rounded-sm text-xs leading-4 font-medium',
                  {
                    'bg-primary text-neutral-800': !mjTile,
                  }
                )}
                onClick={() => setMjTile(false)}
              >
                否
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
